import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import './ManageServices.css';

interface Service {
  id: string;
  icon?: string;
  name: string;
  order?: number;
}

const ManageServices: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState<string>('');
  const [updating, setUpdating] = useState<string | null>(null);
  
  // States for inline editing
  const [editingService, setEditingService] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [updatingName, setUpdatingName] = useState<string | null>(null);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải danh sách ngành nghề...');

      let servicesQuery;
      let querySnapshot;

      try {
        // Thử query với orderBy theo trường order trước
        servicesQuery = query(
          collection(db, 'services'),
          orderBy('order', 'asc')
        );
        console.log('Thử query với orderBy order...');
        querySnapshot = await getDocs(servicesQuery);
      } catch (indexError) {
        console.warn('Query với orderBy thất bại, thử query đơn giản:', indexError);
        
        // Nếu lỗi index, thử query đơn giản hơn
        servicesQuery = collection(db, 'services');
        console.log('Thử query đơn giản...');
        querySnapshot = await getDocs(servicesQuery);
      }

      const servicesData: Service[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Service doc:', doc.id, data);
        
        servicesData.push({
          id: doc.id,
          icon: data.icon || '',
          name: data.name || '',
          order: data.order || 0
        });
      });

      // Sắp xếp thủ công theo order nếu cần
      if (servicesData.length > 0) {
        servicesData.sort((a, b) => {
          const aOrder = a.order || 0;
          const bOrder = b.order || 0;
          return aOrder - bOrder;
        });
      }

      console.log(`Đã tải ${servicesData.length} ngành nghề`);
      setServices(servicesData);
      setFilteredServices(servicesData);
      
    } catch (error: any) {
      console.error('Lỗi chi tiết khi tải danh sách ngành nghề:', error);
      
      let errorMessage = 'Không thể tải danh sách ngành nghề';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Không có quyền truy cập dữ liệu. Vui lòng kiểm tra quyền đăng nhập.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Dịch vụ hiện không khả dụng. Vui lòng thử lại sau.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Cần tạo index cho database. Vui lòng liên hệ quản trị viên.';
      } else if (error.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (servicesToFilter: Service[]) => {
    let filtered = servicesToFilter;

    // Filter theo tên
    if (nameFilter.trim()) {
      filtered = filtered.filter(service =>
        service.name.toLowerCase().includes(nameFilter.trim().toLowerCase())
      );
    }

    return filtered;
  };

  const updateServiceOrder = async (serviceId: string, newOrder: number) => {
    try {
      setUpdating(serviceId);
      const serviceRef = doc(db, 'services', serviceId);
      await updateDoc(serviceRef, {
        order: newOrder
      });

      // Cập nhật state local
      const updatedServices = services.map(service =>
        service.id === serviceId
          ? { ...service, order: newOrder }
          : service
      );
      
      // Sắp xếp lại theo order
      updatedServices.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      setServices(updatedServices);

      // Áp dụng lại filter
      const filtered = applyFilters(updatedServices);
      setFilteredServices(filtered);

    } catch (error) {
      console.error('Lỗi khi cập nhật thứ tự:', error);
      setError('Cập nhật thứ tự thất bại');
    } finally {
      setUpdating(null);
    }
  };

  const startEditingName = (service: Service) => {
    setEditingService(service.id);
    setEditingName(service.name);
  };

  const cancelEditingName = () => {
    setEditingService(null);
    setEditingName('');
  };

  const saveServiceName = async (serviceId: string) => {
    if (!editingName.trim()) {
      alert('Tên ngành nghề không thể để trống');
      return;
    }

    try {
      setUpdatingName(serviceId);
      const serviceRef = doc(db, 'services', serviceId);
      await updateDoc(serviceRef, {
        name: editingName.trim()
      });

      // Cập nhật state local
      const updatedServices = services.map(service =>
        service.id === serviceId
          ? { ...service, name: editingName.trim() }
          : service
      );
      
      setServices(updatedServices);

      // Áp dụng lại filter
      const filtered = applyFilters(updatedServices);
      setFilteredServices(filtered);

      // Reset editing state
      setEditingService(null);
      setEditingName('');

    } catch (error) {
      console.error('Lỗi khi cập nhật tên:', error);
      setError('Cập nhật tên thất bại');
    } finally {
      setUpdatingName(null);
    }
  };

  const handleNameKeyPress = (e: React.KeyboardEvent, serviceId: string) => {
    if (e.key === 'Enter') {
      saveServiceName(serviceId);
    } else if (e.key === 'Escape') {
      cancelEditingName();
    }
  };

  // Effect để fetch data lần đầu
  useEffect(() => {
    fetchServices();
  }, []);

  // Memoize filtering for stability/performance
  const memoFilteredServices = useMemo(() => applyFilters(services), [services, nameFilter]);
  useEffect(() => {
    setFilteredServices(memoFilteredServices);
  }, [memoFilteredServices]);

  if (loading) {
    return (
      <div className="manage-services">
        <div className="page-header">
          <h2>Quản lý ngành nghề</h2>
          <p className="page-subtitle">Quản lý và theo dõi tất cả ngành nghề trong hệ thống</p>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-services">
      <div className="page-header">
        <h2>Quản lý ngành nghề</h2>
        <p className="page-subtitle">Quản lý và theo dõi tất cả ngành nghề trong hệ thống</p>
        <p className="edit-instruction">💡 Click vào Tên ngành nghề hoặc icon ✏️ để chỉnh sửa. Nhấn Enter hoặc click ra ngoài để lưu, Escape để hủy.</p>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="name-filter">Tên ngành nghề:</label>
          <input
            id="name-filter"
            type="text"
            placeholder="Tìm theo tên ngành nghề..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>

        <button onClick={fetchServices} className="refresh-button">
          Làm mới
        </button>
      </div>

      {error && (
        <div className="error-message">
          <div>
            <strong>{error}</strong>
            <br />
            <small>💡 Mở Developer Console (F12) để xem thông tin chi tiết về lỗi</small>
          </div>
          <button 
            onClick={() => {
              setError(null);
              fetchServices();
            }}
            className="retry-button"
          >
            Thử lại
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="services-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Tên ngành nghề</th>
              <th>Sắp xếp</th>
            </tr>
          </thead>
          <tbody>
            {filteredServices.length === 0 ? (
              <tr>
                <td colSpan={3} className="no-data">
                  {services.length === 0 ? 'Không có ngành nghề nào' : 'Không tìm thấy ngành nghề phù hợp'}
                </td>
              </tr>
            ) : (
              filteredServices.map((service, index) => (
                <tr key={service.id}>
                  <td className="order-cell">
                    <span className="order-display">{service.order || 0}</span>
                  </td>
                  <td className="service-name">
                    {editingService === service.id ? (
                      <div className="name-edit-container">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => handleNameKeyPress(e, service.id)}
                          onBlur={() => saveServiceName(service.id)}
                          className="name-edit-input"
                          autoFocus
                          disabled={updatingName === service.id}
                        />
                        {updatingName === service.id && (
                          <span className="updating">Đang lưu...</span>
                        )}
                      </div>
                    ) : (
                      <div className="editable-container">
                        <strong
                          className="service-name-text editable"
                          onClick={() => startEditingName(service)}
                          title="Click để chỉnh sửa"
                        >
                          {service.name}
                        </strong>
                        <button
                          className="edit-icon-btn"
                          onClick={() => startEditingName(service)}
                          title="Chỉnh sửa tên"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td>
                    <input
                      type="number"
                      value={service.order || 0}
                      onChange={(e) => updateServiceOrder(service.id, parseInt(e.target.value) || 0)}
                      disabled={updating === service.id}
                      className="order-input"
                      min="0"
                      placeholder="Thứ tự"
                    />
                    {updating === service.id && (
                      <span className="updating">Đang cập nhật...</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageServices; 